import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';
import { useSetHomePreference } from '@/hooks/use-home-preference';
import type { HomePageDef, HomePageId } from './home-registry';

/**
 * Segmented control in the home header for users whose location has more
 * than one module home page. Picking one saves it as the default, so Home
 * keeps opening there — the same setting the Settings page exposes.
 */
export function HomeSwitcher({ pages, activeId }: { pages: readonly HomePageDef[]; activeId: HomePageId }) {
  const save = useSetHomePreference();
  return (
    <div
      role="radiogroup"
      aria-label="Home page"
      className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-border bg-white p-0.5"
    >
      {pages.map((p) => {
        const active = p.id === activeId;
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={save.isPending}
            onClick={() => {
              if (active) return;
              save.mutate(p.id, {
                onError: (err: any) => toast.error(err?.error?.message || 'Could not change home page'),
              });
            }}
            className={cn(
              'whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70',
              active
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
